import React, { useState } from 'react';
import { CheckCircle, XCircle, Loader2, AlertCircle } from 'lucide-react';
import { apiRequest } from '@/lib/api-client';
import { checkSubscriptionAccess } from '@/lib/subscription-gate';
import s from './ai.module.css';
import type { ConfirmationData } from '@/lib/ai-tools';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ConfirmationCardProps {
  confirmation: ConfirmationData & { status: 'pending' | 'confirmed' | 'cancelled' };
  onResolved: (status: 'confirmed' | 'cancelled', resultMessage: string) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ConfirmationCard: React.FC<ConfirmationCardProps> = ({ confirmation, onResolved }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  async function handleConfirm() {
    if (!checkSubscriptionAccess()) return;
    setIsLoading(true);
    setApiError(null);
    try {
      await apiRequest(confirmation.endpoint, {
        method: confirmation.method,
        body: JSON.stringify(confirmation.body),
      });
      onResolved('confirmed', `Done — ${confirmation.action}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Request failed';
      setApiError(message);
      // Still mark confirmed even on error so we can show the error inline
      onResolved('confirmed', `Error: ${message}`);
    } finally {
      setIsLoading(false);
    }
  }

  function handleCancel() {
    onResolved('cancelled', 'Cancelled.');
  }

  const isPending = confirmation.status === 'pending';

  return (
    <div className={s.confirmCard}>
      {/* Action description */}
      <div>
        <p className={s.confirmLabel}>Confirm action</p>
        <p className={s.confirmAction}>{confirmation.action}</p>
      </div>

      {/* Input preview */}
      {Object.keys(confirmation.input).length > 0 && (
        <div className={s.confirmPreview}>
          {Object.entries(confirmation.input).map(([key, value]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span className={s.confirmPreviewKey}>
                {key.replace(/_/g, ' ')}:
              </span>
              <span className={s.confirmPreviewVal}>
                {String(value)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {apiError && (
        <div className={s.confirmError}>
          <AlertCircle size={12} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{apiError}</span>
        </div>
      )}

      {/* Actions — only shown when pending */}
      {isPending && (
        <div className={s.confirmActions}>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className={s.confirmBtnConfirm}
          >
            {isLoading ? (
              <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <CheckCircle size={12} />
            )}
            Confirm
          </button>

          <button
            onClick={handleCancel}
            disabled={isLoading}
            className={s.confirmBtnCancel}
          >
            <XCircle size={12} />
            Cancel
          </button>
        </div>
      )}

      {/* Resolved state badge */}
      {!isPending && (
        <div className={confirmation.status === 'confirmed' ? s.confirmResolvedOk : s.confirmResolvedCancel}>
          {confirmation.status === 'confirmed' ? (
            <CheckCircle size={12} />
          ) : (
            <XCircle size={12} />
          )}
          {confirmation.status === 'confirmed' ? 'Confirmed' : 'Cancelled'}
        </div>
      )}
    </div>
  );
};

// Keep legacy export shape so existing code that imported ConfirmationCardData doesn't break
export type { ConfirmationData as ConfirmationCardData };
